// =============================================================================
// SimSense Platform — Teams Page
// =============================================================================

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../utils/api";
import { useToast } from "../components/Toast";



export function Teams() {
  const [memberships, setMemberships] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewNameDesc] = useState("");
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    document.title = "Teams | SimSense";
    async function fetchTeams() {
      try {
        const data = await api<any[]>("/api/teams/me");
        setMemberships(data);
      } catch (err) {
        console.error("Failed to fetch teams", err);
      } finally {
        setLoading(false);
      }
    }
    fetchTeams();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    
    try {
      const team = await api<any>("/api/teams/", {
        method: "POST",
        body: JSON.stringify({
          name: newName,
          description: newDesc,
          isPublic: true
        })
      });
      setMemberships([...memberships, { team, role: "owner" }]);
      setShowCreate(false);
      setNewName("");
      setNewNameDesc("");
    } catch (err) {
      toast.error("Failed to create team.");
    }
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
        <div>
          <h2>Teams</h2>
          <p style={{ color: "var(--text2)", fontSize: "14px" }}>Manage your racing teams and community groups.</p>
        </div>
        <button 
          onClick={() => setShowCreate(true)}
          style={{
            padding: "8px 16px",
            background: "var(--accent)",
            color: "#fff",
            border: "none",
            borderRadius: "var(--radius-sm)",
            fontWeight: 600,
            cursor: "pointer"
          }}
        >
          + Create Team
        </button>
      </div>

      {showCreate && (
        <div style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)", marginBottom: "32px" }}>
          <h3 style={{ marginBottom: "16px" }}>New Team</h3>
          <form onSubmit={handleCreate} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", color: "var(--text2)", marginBottom: "4px" }}>Team Name</label>
              <input 
                type="text" 
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. Team Apex Racing"
                style={{ width: "100%", padding: "10px", background: "rgba(0,0,0,0.2)", border: "1px solid var(--border)", borderRadius: "4px", color: "#fff" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", color: "var(--text2)", marginBottom: "4px" }}>Description (Optional)</label>
              <textarea 
                value={newDesc}
                onChange={e => setNewNameDesc(e.target.value)}
                style={{ width: "100%", padding: "10px", background: "rgba(0,0,0,0.2)", border: "1px solid var(--border)", borderRadius: "4px", color: "#fff", height: "80px" }}
              />
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button type="submit" style={{ padding: "8px 24px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: "4px", fontWeight: 600, cursor: "pointer" }}>Create</button>
              <button type="button" onClick={() => setShowCreate(false)} style={{ padding: "8px 24px", background: "none", border: "1px solid var(--border)", color: "var(--text2)", borderRadius: "4px", cursor: "pointer" }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: "center", padding: "40px" }}>Loading your teams...</div>
      ) : memberships.length === 0 ? (
        <div style={{ textAlign: "center", padding: "80px 24px", background: "var(--card)", borderRadius: "var(--radius)", border: "1px dashed var(--border)" }}>
          <div style={{ fontSize: "40px", marginBottom: "16px" }}>🏎️</div>
          <h3>No teams yet</h3>
          <p style={{ color: "var(--text2)", maxWidth: "400px", margin: "0 auto 24px" }}>
            Teams allow you to share telemetry, setups, and benchmarks with a private group of drivers.
          </p>
          <button 
            onClick={() => setShowCreate(true)}
            style={{ padding: "10px 20px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: "4px", fontWeight: 600, cursor: "pointer" }}
          >
            Create Your First Team
          </button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: "20px" }}>
          {memberships.map((m) => (
            <div 
              key={m.team.id}
              onClick={() => navigate(`/teams/${m.team.id}`)}
              style={{
                background: "var(--card)",
                padding: "20px",
                borderRadius: "var(--radius)",
                border: "1px solid var(--border)",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                cursor: "pointer",
                transition: "transform 0.2s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-4px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div style={{ fontSize: "24px" }}>🏁</div>
                <span style={{ fontSize: "10px", fontWeight: 700, padding: "2px 6px", background: "var(--border)", borderRadius: "4px", textTransform: "uppercase" }}>{m.role}</span>
              </div>
              <div>
                <h4 style={{ fontSize: "18px", marginBottom: "4px" }}>{m.team.name}</h4>
                <p style={{ fontSize: "13px", color: "var(--text2)", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                  {m.team.description || "No description provided."}
                </p>
              </div>
              <div style={{ marginTop: "8px", paddingTop: "16px", borderTop: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "12px", color: "var(--text3)" }}>Member since {new Date(m.joinedAt).toLocaleDateString()}</span>
                <span style={{ color: "var(--accent)", fontSize: "12px", fontWeight: 600 }}>
                  VIEW →
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
